import React, { useState } from 'react'
import NextImage from 'next/image'
import { useTranslation } from 'next-i18next'
import { styled } from '@mui/material/styles'
import { isValidHttpUrl } from '@/utils'
import { useFormValues, useIsQueryOwner } from '@/hooks'
import { useEMTDispatch } from '@/store'
import { UserServiceHelper } from '@/modules/User'
import { useNotifications } from '@/modules/Notifications'
import FileDropZone from '@/components/EMT/FileDropZone'
import {
  EmtButton,
  EmtFloatingIconButton,
  EmtIcon,
  EmtModal,
  EmtSeparator,
  EmtTheme,
  EmtTypography,
  useLargeTablet,
  useMobile,
} from 'emt-components-v2'
import { ValidationModal } from '@/components/Profile/Modals'
import Box from '@mui/material/Box'
import AddImagePng from '@/assets/img/add-image.png'
import Blur from '@/assets/img/blur.png'
import { Picture } from '@/dataV3/Common/Picture'
import useCurrentUser from '@/hooks/useCurrentUser'

type Props = React.PropsWithChildren<{
  pictures: Picture[]
  open: boolean
  onClose: () => void
  animalId?: string
}>

type FileType = string | ArrayBuffer | null
type FormValues = {
  images: Array<FileType>
  avatar: FileType
}

// CONFIG
const gap = 12
const sidePanelGap = `${gap}px`
const avatarSize = 200
const mobileAvatarSize = 210
const otherImagesSize = avatarSize / 2 - gap / 2
const mobileOtherImagesSize = mobileAvatarSize / 2 - gap / 2

type EmtThemeType = { theme: EmtTheme }

const Sidebar = styled(Box)<EmtThemeType>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: sidePanelGap,
  padding: '24px',
  paddingRight: 0,
  background: theme.palette.common.white,
  maxHeight: 700,
  overflowX: 'hidden',
  position: 'relative',
  paddingBottom: '80px',
  scrollbarColor: '#D2D1D4 transparent',
  '&::-webkit-scrollbar': {
    width: '5px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#D2D1D4',
    borderRadius: '8px',
  },
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    flexDirection: 'columns',
    rowGap: '20px',
    maxHeight: 'inherit',
    background: theme.palette.greyGradient[50],
  },
})) as typeof Box

const StyledImageView = styled(Box)<EmtThemeType>(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  height: '80vh',
  width: '100%',
  maxHeight: '700px',
  overflow: 'hidden',
  '& img': {
    objectFit: 'contain',
  },
  [theme.breakpoints.down('md')]: {
    position: 'fixed',
    top: 0,
    left: 0,
    background: theme.palette.greyGradient[100],
    height: '100%',
    maxHeight: 'inherit',
    marginLeft: '0',
    marginRight: '0',
    zIndex: 9999,
  },
})) as typeof Box

export const EditImagesModal: React.FC<Props> = ({ open, onClose, pictures, animalId }) => {
  const [isMobile, isTablet] = useMobile()
  const isLargeTablet = useLargeTablet()

  const { t } = useTranslation('profile')
  const dispatch = useEMTDispatch()
  const { avatar } = useCurrentUser()
  const [isQueryOwner] = useIsQueryOwner()
  const [addNotificationError, addNotificationSuccess] = useNotifications()

  const [currentImageView, setCurrentImageView] = useState<string | FileType>()
  const [onSubmitLoader, setOnSubmitLoader] = useState<boolean>(false)
  const [canSave, setCanSave] = useState<boolean>(false)
  const [showMobileImageView, setShowMobileImageView] = useState<boolean>(false)

  const [hasChange, setHasChange] = useState<boolean>(false)
  const [activeCancelModal, setActiveCancelModal] = useState<boolean>(false)
  const [activeDeleteImageModal, setActiveDeleteImageModal] = useState<boolean>(false)

  const avatarImage = pictures?.find((picture) => picture.isMain)
  const profileImages = pictures?.filter((picture) => !picture.isMain) || []

  const { values, setValue, setError } = useFormValues<FormValues>({
    images: [null, null, null, null, null, null, null, null, null, null],
    avatar: null,
  })

  const reorderImages = (images: Array<FileType>) => {
    const nullImages = images.filter((img) => img === null)
    const noEmptyImages = images.filter((img) => img !== null)
    return [...noEmptyImages, ...nullImages]
  }

  const handleDelete = () => {
    if (values.avatar === currentImageView) {
      setValue('avatar', null)
      setValue('images', reorderImages(values.images))
    }
    if (values.images.includes(currentImageView as FileType)) {
      setValue('images', (prev: FileType[]) => {
        const [...newImages] = prev
        newImages[values.images.indexOf(currentImageView as FileType)] = null
        checkField('images', newImages)
        return reorderImages(newImages)
      })
    }
    setCurrentImageView(undefined)
    setCanSave(true)
    setHasChange(true)
    if (isMobile) setShowMobileImageView(false)
  }

  const onAlbumDrop = (index: number) => (file: FileType) => {
    setCurrentImageView(file)
    setValue('images', (prev: FileType[]) => {
      const newImages = [...prev]
      newImages[index] = file
      return newImages
    })
    setCanSave(true)
    setHasChange(true)
  }

  const handleDropZoneClick = (openFunction: () => void, imageUrl: string | FileType) => {
    setCurrentImageView(imageUrl)
    if (isMobile) setShowMobileImageView(true)
  }

  const onFieldChange = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setValue(key, value)
    checkField(key, value)
  }

  const checkField = <K extends keyof FormValues>(key: K, value: FormValues[K] = values[key]): boolean => {
    switch (key) {
      case 'avatar':
        if (!value) {
          setError(key, t(`create-your-profile.address-photo.errors.${key}`))
          return false
        }
        setError(key, '')
        return true
      default:
        return false
    }
  }

  const onSubmit = async () => {
    if (!values.avatar) return

    try {
      if (typeof values.avatar === 'string' && !isValidHttpUrl(values.avatar)) {
        try {
          if (avatar) {
            await UserServiceHelper.deletePictureAsync(avatar.id)
          }
          if (animalId) {
            await UserServiceHelper.addAnimalPictureAsync(animalId, { base64: values.avatar, isMain: true })
          } else {
            await UserServiceHelper.addMemberPictureAsync({ base64: values.avatar, isMain: true })
          }
        } catch (error) {
          const fields = addNotificationError({
            error,
          })
          setError('avatar', fields.image || '')
          console.warn('Update Avatar Error', error.message, fields)
        }
      }

      if (profileImages?.length) {
        for (const profileImage of profileImages) {
          if (!values.images.includes(profileImage.url)) {
            await UserServiceHelper.deletePictureAsync(profileImage.id)
          }
        }
      }

      for (const image of values.images) {
        if (image && typeof image === 'string' && !isValidHttpUrl(image)) {
          if (animalId) {
            await UserServiceHelper.addAnimalPictureAsync(animalId, { base64: image, isMain: false })
          } else {
            await UserServiceHelper.addMemberPictureAsync({ base64: image, isMain: false })
          }
        }
      }
    } catch (error) {
      addNotificationError({
        error,
      })
      console.warn('Update Images Error', error.message)
    }
  }

  const handleSubmit = () => {
    setOnSubmitLoader(true)
    onSubmit().then(async () => {
      await UserServiceHelper.dispatchGetMyInfoAsync(dispatch)()
      addNotificationSuccess({
        title: t('alerts.successUploadImage.title') as string,
        message: t('alerts.successUploadImage.message') as string,
      })
      setOnSubmitLoader(false)
      setCanSave(false)
      setHasChange(false)
      onClose()
    })
  }

  React.useEffect(() => {
    if (avatarImage) setValue('avatar', avatarImage.url)
    const images: (string | null)[] = profileImages ? profileImages.map((profileImage) => profileImage.url) : []
    for (let i = 0; i < 10; i++) {
      if (!images[i]) {
        images[i] = null
      }
    }
    setValue('images', images)
    setCurrentImageView(avatarImage ? avatarImage.url : undefined)
    setActiveCancelModal(false)
    setActiveDeleteImageModal(false)
    setHasChange(false)
    setCanSave(false)
  }, [open])

  const ImageView = (
    <StyledImageView>
      {/* 📷 Image display 📷 */}
      {currentImageView ? (
        <NextImage
          placeholder="blur"
          blurDataURL={Blur.blurDataURL}
          loader={() => currentImageView as string}
          src={currentImageView as string}
          quality={70}
          layout="fill"
          alt={''}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <NextImage src={AddImagePng} quality={70} alt={''} />
        </Box>
      )}
      {/* 📷 Image display 📷 */}

      {/* 🗑️ Delete Button 🗑️ */}
      {isMobile && currentImageView ? (
        <Box sx={{ position: 'absolute', left: 20, bottom: 30 }} className="EmtReactNativeWebViewPaddingBottom">
          <EmtFloatingIconButton
            boxShadow
            iconClass="emt-bin"
            sx={{ cursor: 'pointer', width: '48px', height: '48px', '& .emt-icons': { fontSize: '21px' } }}
            onClick={() => setActiveDeleteImageModal(true)}
          />
        </Box>
      ) : null}
      {/* 🗑️ END Delete Button 🗑️ */}

      {isMobile ? (
        <EmtIcon
          className="emt-x EmtReactNativeWebViewMarginTop"
          sx={{
            top: '25px !important',
            right: '25px !important',
            color: '#1D1929 !important',
          }}
          onClick={() => setShowMobileImageView(false)}
        />
      ) : null}
    </StyledImageView>
  )

  return (
    <EmtModal
      open={open}
      variant="carousel"
      onClose={() => {
        !onSubmitLoader && hasChange ? setActiveCancelModal(!onSubmitLoader) : null
        !onSubmitLoader && !hasChange ? onClose() : null
      }}
      sx={{
        '& .children-box': { padding: 0, overflow: 'hidden', borderRadius: '24px' },
        '& .inner-box': {
          width: isMobile ? '100%' : isLargeTablet ? '90%' : '80%',
          maxWidth: 1040,
          maxHeight: isMobile ? '100%' : 740,
          ...(isMobile || isTablet ? { height: '100%' } : {}),
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          height: isMobile ? '100%' : 'auto',
        }}
        className="EmtReactNativeWebViewMarginTop EmtReactNativeWebViewPaddingBottom"
      >
        {/* 🗑️ Delete Button 🗑️ */}
        {!isMobile && currentImageView ? (
          <Box
            sx={{ position: 'absolute', left: 20, bottom: 20, zIndex: 1 }}
            className="EmtReactNativeWebViewPaddingBottom"
          >
            <EmtFloatingIconButton
              boxShadow
              iconClass="emt-bin"
              sx={{
                cursor: 'pointer',
                width: '48px',
                height: '48px',
                '& .emt-icons': { fontSize: '21px' },
              }}
              onClick={() => setActiveDeleteImageModal(true)}
            />
          </Box>
        ) : null}
        {/* 🗑️ END Delete Button 🗑️ */}

        {/* 🖼️ Image view 🖼️ */}
        {!isMobile ? ImageView : null}
        {/* 🖼️ END Image view 🖼️ */}

        {/* 🔧 Sidebar 🔧 */}
        <Sidebar sx={{ width: isMobile ? '100%' : isTablet ? 700 : isLargeTablet ? 600 : 560 }}>
          {isMobile ? (
            <Box sx={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <EmtFloatingIconButton
                boxShadow
                iconClass="emt-arrow-left"
                sx={{
                  cursor: 'pointer',
                  width: '40px',
                  height: '40px',
                  '& .emt-icons': { fontSize: '18px' },
                }}
                onClick={!onSubmitLoader ? onClose : undefined}
              />
              <EmtTypography variant="h4" fontWeight={800}>
                {t('images.edit.title')}
              </EmtTypography>
            </Box>
          ) : null}

          {!isMobile ? (
            <EmtTypography variant="h4" fontWeight={800}>
              {t('images.edit.title')}
            </EmtTypography>
          ) : null}

          <EmtSeparator sx={{ margin: '10px 0' }} />

          <EmtTypography variant="h5" fontWeight={700}>
            {t('images.edit.subtitle')}
          </EmtTypography>

          <EmtTypography variant="body1"> {t('images.edit.text')}</EmtTypography>

          {!values.avatar ? (
            <EmtTypography variant="body1" color="error">
              {t('images.edit.error.avatar')}
            </EmtTypography>
          ) : null}

          {!isMobile ? (
            <>
              <Box sx={{ display: 'flex', gap: sidePanelGap }}>
                {/* 👤 Avatar 👤 */}
                <FileDropZone
                  variant="default"
                  isOwner={isQueryOwner}
                  preview={values.avatar as any}
                  maxFiles={1}
                  multipleFiles={false}
                  onDrop={(file) => {
                    onFieldChange('avatar', file)
                    setCurrentImageView(file)
                    setHasChange(true)
                    setCanSave(true) // Enable the save button
                  }}
                  overrideDropZoneBox={{
                    width: isMobile ? mobileAvatarSize : avatarSize,
                    height: isMobile ? mobileAvatarSize : avatarSize,
                    border: (theme: any) =>
                      values.avatar === currentImageView ? `2px solid ${theme.palette.primary.main}` : null,
                    transition: 'none',
                  }}
                  activeDeleteButton={false}
                  activeRotation={false}
                  overrideOpenImageDialog={
                    values.avatar ? (openFunction) => handleDropZoneClick(openFunction, values.avatar) : undefined
                  }
                />
                {/* 👤 END Avatar 👤 */}

                {/* 3️⃣ First 2 images 3️⃣ */}
                <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: sidePanelGap }}>
                  {values.images.map(
                    (v, k) =>
                      k <= 1 && (
                        <FileDropZone
                          key={k}
                          variant={values.images.findIndex((v) => v === null) === k ? 'default' : 'small'}
                          isOwner={isQueryOwner}
                          maxFiles={1}
                          multipleFiles={false}
                          onDrop={onAlbumDrop(k)}
                          overrideDropZoneBox={{
                            width: isMobile ? mobileOtherImagesSize : otherImagesSize,
                            height: isMobile ? mobileOtherImagesSize : otherImagesSize,
                            border: (theme: any) =>
                              values.images[k] === currentImageView ? `2px solid ${theme.palette.primary.main}` : null,
                            transition: 'none',
                          }}
                          activeDeleteButton={false}
                          activeRotation={false}
                          preview={values.images[k] as any}
                          overrideOpenImageDialog={
                            values.images[k]
                              ? (openFunction) => handleDropZoneClick(openFunction, values.images[k])
                              : undefined
                          }
                        />
                      ),
                  )}
                </Box>
                {/* 3️⃣ END First 2 images 3️⃣ */}
              </Box>

              {/* 8️⃣ Other images 8️⃣ */}
              <Box sx={{ display: 'flex', gap: sidePanelGap, flexWrap: 'wrap' }}>
                {values.images.map(
                  (v, k) =>
                    k > 1 && (
                      <FileDropZone
                        key={k}
                        variant={values.images.findIndex((v) => v === null) === k ? 'default' : 'small'}
                        isOwner={isQueryOwner}
                        maxFiles={1}
                        multipleFiles={false}
                        overrideDropZoneBox={{
                          width: isMobile ? mobileOtherImagesSize : otherImagesSize,
                          height: isMobile ? mobileOtherImagesSize : otherImagesSize,
                          border: (theme: any) =>
                            values.images[k] === currentImageView ? `2px solid ${theme.palette.primary.main}` : null,
                          transition: 'none',
                        }}
                        activeDeleteButton={false}
                        activeRotation={false}
                        preview={values.images[k] as any}
                        onDrop={onAlbumDrop(k)}
                        overrideOpenImageDialog={
                          values.images[k]
                            ? (openFunction) => handleDropZoneClick(openFunction, values.images[k])
                            : undefined
                        }
                      />
                    ),
                )}
              </Box>
              {/* 8️⃣ END Other images 8️⃣ */}
            </>
          ) : (
            <Box>
              <Box sx={{ float: 'left', paddingRight: '15px', marginBottom: '10px' }}>
                {/* 👤 Avatar 👤 */}
                <FileDropZone
                  variant="default"
                  isOwner={isQueryOwner}
                  preview={values.avatar as any}
                  maxFiles={1}
                  multipleFiles={false}
                  onDrop={(file) => {
                    onFieldChange('avatar', file)
                    setCurrentImageView(file)
                    setHasChange(true)
                    setCanSave(true) // Enable the save button
                  }}
                  overrideDropZoneBox={{
                    width: isMobile ? mobileAvatarSize : avatarSize,
                    height: isMobile ? mobileAvatarSize : avatarSize,
                    border: (theme: any) =>
                      values.avatar === currentImageView ? `2px solid ${theme.palette.primary.main}` : null,
                    transition: 'none',
                  }}
                  activeDeleteButton={false}
                  activeRotation={false}
                  overrideOpenImageDialog={
                    values.avatar ? (openFunction) => handleDropZoneClick(openFunction, values.avatar) : undefined
                  }
                />
                {/* 👤 END Avatar 👤 */}
              </Box>

              {values.images.map((v, k) => (
                <Box key={k} sx={{ float: 'left', padding: '15px', paddingTop: 0, paddingLeft: 0 }}>
                  <FileDropZone
                    variant={values.images.findIndex((v) => v === null) === k ? 'default' : 'small'}
                    isOwner={isQueryOwner}
                    maxFiles={1}
                    multipleFiles={false}
                    onDrop={onAlbumDrop(k)}
                    overrideDropZoneBox={{
                      width: isMobile ? mobileOtherImagesSize : otherImagesSize,
                      height: isMobile ? mobileOtherImagesSize : otherImagesSize,
                      border: (theme: any) =>
                        values.images[k] === currentImageView ? `2px solid ${theme.palette.primary.main}` : null,
                      transition: 'none',
                    }}
                    activeDeleteButton={false}
                    activeRotation={false}
                    preview={values.images[k] as any}
                    overrideOpenImageDialog={
                      values.images[k]
                        ? (openFunction) => handleDropZoneClick(openFunction, values.images[k])
                        : undefined
                    }
                  />
                </Box>
              ))}
            </Box>
          )}

          {isMobile && showMobileImageView ? ImageView : null}

          {/* 💾 Save Button 💾 */}
          <Box
            sx={{
              position: 'fixed',
              bottom: '20px',
              width: isMobile ? '80%' : 305,
              right: isMobile ? null : '2.5%',
              left: isMobile ? '50%' : null,
              transform: isMobile ? 'translateX(-50%)' : null,
            }}
            className="EmtReactNativeWebViewPaddingBottom"
          >
            <EmtButton
              fullWidth
              color="primary"
              size="large"
              startIcon={<EmtIcon className="emt-save" />}
              disabled={!values.avatar || !canSave}
              onClick={handleSubmit}
              loader={onSubmitLoader}
            >
              {t('profileSidebar.save')}
            </EmtButton>
          </Box>
          {/* 💾 END Save Button 💾 */}
        </Sidebar>
        {/* 🔧 END Sidebar 🔧 */}

        <ValidationModal
          open={activeCancelModal}
          title={t('modals.validationAbortEdit.title')}
          text={t('modals.validationAbortEdit.text')}
          confirmButtonLabel={t('modals.validationAbortEdit.confirmButtonLabel')}
          rejectButtonLabel={t('modals.validationAbortEdit.rejectButtonLabel')}
          icon={'emt-exclamation-circle'}
          onConfirm={onClose}
          onReject={() => setActiveCancelModal(false)}
        />

        <ValidationModal
          open={activeDeleteImageModal}
          title={t('modals.validationDeletePicture.title')}
          text={t('modals.validationDeletePicture.text')}
          confirmButtonLabel={t('modals.validationDeletePicture.confirmButtonLabel')}
          rejectButtonLabel={t('modals.validationDeletePicture.rejectButtonLabel')}
          icon={'emt-exclamation-circle'}
          onConfirm={() => {
            handleDelete()
            setActiveDeleteImageModal(false)
          }}
          onReject={() => setActiveDeleteImageModal(false)}
        />
      </Box>
    </EmtModal>
  )
}
